import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Badge,
  Table,
  ButtonGroup
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API } from '../../config/api';
import { formatCoreSpec } from '../../helpers/productLabel';
import {
  faPlus,
  faRotate,
  faArrowLeft,
  faWarehouse,
  faBoxOpen,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';

// eng-backend :5000 — where /api/v1/warehouse + /api/v1/standard live.
const API_BASE = `${API.BACKEND}/api/v1`;
const http = axios.create({ baseURL: API_BASE, timeout: 20000 });
http.interceptors.request.use(c => {
  const t = localStorage.getItem('token');
  if (t) c.headers['x-auth-token'] = t;
  return c;
});

const COLOR_HEX = {
  BLACK: '#1a1a1a', BLUE: '#2f7cff', BROWN: '#8b5a2b',
  'GREEN-YELLOW': '#9acd32', GREEN: '#2e8b57', GREY: '#7a7a7a',
  GRAY: '#7a7a7a', RED: '#d9534f', YELLOW: '#f0c400', ORANGE: '#f39c12',
  WHITE: '#e8e8e8', NATUR: '#efe6d4'
};
const DRUM_MATERIALS = ['wood', 'metal', 'plastic'];
const DRUM_DIAMETERS = [400, 500, 630, 710, 800, 1000, 1250, 1400];
const fmt = n => Number(n || 0).toLocaleString();

const variantLabel = v =>
  `${formatCoreSpec(v)}${v.color ? ` · ${String(v.color).toUpperCase()}` : ''}`;

export default function WarehouseSeed() {
  const navigate = useNavigate();

  const [names, setNames] = useState([]);
  const [name, setName] = useState('');
  const [variants, setVariants] = useState([]);
  const [variantIdx, setVariantIdx] = useState('');

  const [kind, setKind] = useState('drum'); // 'drum' | 'coils'
  const [length, setLength] = useState(6000);
  const [drumMaterial, setDrumMaterial] = useState('wood');
  const [drumDiameter, setDrumDiameter] = useState(1000);
  const [coilLength, setCoilLength] = useState(100);
  const [coilCount, setCoilCount] = useState(10);

  const [owner, setOwner] = useState('stock'); // 'stock' | 'client'
  const [customerName, setCustomerName] = useState('');
  const [quantity, setQuantity] = useState(1);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState([]);

  const [syncBusy, setSyncBusy] = useState(false);
  const [syncReport, setSyncReport] = useState(null);

  // cable names
  useEffect(() => {
    http
      .get('/standard?name=all')
      .then(r => {
        const list = r.data?.data || r.data || [];
        const uniq = [
          ...new Set(
            (Array.isArray(list) ? list : [])
              .map(s => (typeof s === 'string' ? s : s?.name))
              .filter(Boolean)
          )
        ].sort();
        setNames(uniq);
      })
      .catch(() => setNames([]));
  }, []);

  // variants for the chosen name
  useEffect(() => {
    if (!name) {
      setVariants([]);
      setVariantIdx('');
      return;
    }
    http
      .get(`/standard?name=${encodeURIComponent(name)}`)
      .then(r => {
        const list = (r.data?.data || r.data || []).filter(Boolean);
        list.sort(
          (a, b) =>
            Number(a.numberOfConductors) - Number(b.numberOfConductors) ||
            Number(a.section) - Number(b.section) ||
            String(a.color || '').localeCompare(String(b.color || ''))
        );
        setVariants(list);
        setVariantIdx(list.length ? '0' : '');
      })
      .catch(() => setVariants([]));
  }, [name]);

  const variant = variants[Number(variantIdx)] || null;

  const submit = async () => {
    if (!variant) {
      setError('Pick a cable + variant first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = {
        kind,
        cableName: variant.name,
        section: variant.section,
        numberOfConductors: variant.numberOfConductors,
        color: variant.color,
        standardId: variant._id || variant.standardId || null,
        owner,
        customerName: owner === 'client' ? customerName : null,
        quantity: Number(quantity) || 1
      };
      if (kind === 'drum') {
        body.length = Number(length);
        body.drumMaterial = drumMaterial;
        body.drumDiameter = Number(drumDiameter);
      } else {
        body.coilLength = Number(coilLength);
        body.coilCount = Number(coilCount);
      }
      const res = await http.post('/warehouse/seed', body);
      setCreated(prev => [...(res.data?.items || []), ...prev].slice(0, 60));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const runSync = async () => {
    setSyncBusy(true);
    setSyncReport(null);
    setError(null);
    try {
      const res = await http.post('/warehouse/sync');
      setSyncReport(res.data?.report || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSyncBusy(false);
    }
  };

  return (
    <Container fluid className="py-3">
      <Row className="align-items-center mb-3 g-2">
        <Col>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faWarehouse} className="text-primary me-2" />
            Warehouse stock — add / sync
          </h4>
          <div className="text-body-tertiary small">
            Add finished cable on drums or coils on europallets. Items get the exact
            same structure as real produced goods — usable end-to-end (orders, tasks,
            shipping) without launching &amp; simulating.
          </div>
        </Col>
        <Col xs="auto">
          <Button
            variant="outline-secondary"
            size="sm"
            className="me-2"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={runSync}
            disabled={syncBusy}
            title="Reconcile the warehouse with production — bridge finished drums that were missed"
          >
            <FontAwesomeIcon icon={faRotate} spin={syncBusy} className="me-1" />
            {syncBusy ? 'Syncing…' : 'Sync from production'}
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="py-2">
          {error}
        </Alert>
      )}

      {syncReport && (
        <Alert
          variant={syncReport.orphans > 0 ? 'warning' : 'info'}
          dismissible
          onClose={() => setSyncReport(null)}
          className="py-2"
        >
          <span className="fw-semibold">Sync:</span>{' '}
          <span className="text-success">{syncReport.bridged} bridged</span> ·{' '}
          {syncReport.alreadyPresent} present · {syncReport.orphans} orphan(s) ·{' '}
          {syncReport.productionFinalDrums} final drum(s) in production
        </Alert>
      )}

      <Row className="g-3">
        {/* ── FORM ─────────────────────────────────────────── */}
        <Col lg={5}>
          <Card className="shadow-sm">
            <Card.Header className="bg-body-emphasis fw-semibold">
              <FontAwesomeIcon icon={faPlus} className="me-2" /> Add stock
            </Card.Header>
            <Card.Body>
              {/* cable + variant */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold fs-9">Cable</Form.Label>
                <Form.Select value={name} onChange={e => setName(e.target.value)}>
                  <option value="">— select cable —</option>
                  {names.map(nm => (
                    <option key={nm} value={nm}>{nm}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold fs-9">Variant</Form.Label>
                <Form.Select
                  value={variantIdx}
                  onChange={e => setVariantIdx(e.target.value)}
                  disabled={!variants.length}
                >
                  {!variants.length && <option value="">— pick a cable first —</option>}
                  {variants.map((v, i) => (
                    <option key={v._id || i} value={String(i)}>
                      {variantLabel(v)}
                    </option>
                  ))}
                </Form.Select>
                {variant?.color && (
                  <span
                    className="badge rounded-pill mt-2 fw-semibold"
                    style={{
                      background: COLOR_HEX[String(variant.color).toUpperCase()] || '#8892a0',
                      color:
                        String(variant.color).toUpperCase() === 'WHITE' ||
                        String(variant.color).toUpperCase() === 'YELLOW'
                          ? '#111'
                          : '#fff',
                      border: '1px solid rgba(0,0,0,0.15)'
                    }}
                  >
                    {String(variant.color).toUpperCase()}
                  </span>
                )}
              </Form.Group>

              {/* kind toggle */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold fs-9 d-block">Form</Form.Label>
                <ButtonGroup>
                  <Button
                    variant={kind === 'drum' ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => setKind('drum')}
                  >
                    <FontAwesomeIcon icon={faBoxOpen} className="me-1" /> Cable on drum
                  </Button>
                  <Button
                    variant={kind === 'coils' ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => setKind('coils')}
                  >
                    <FontAwesomeIcon icon={faLayerGroup} className="me-1" /> Coils on pallet
                  </Button>
                </ButtonGroup>
              </Form.Group>

              {/* drum vs coils fields */}
              {kind === 'drum' ? (
                <Row className="g-2 mb-3">
                  <Col xs={4}>
                    <Form.Label className="fw-semibold fs-9">Length (m)</Form.Label>
                    <Form.Control
                      type="number"
                      value={length}
                      min={1}
                      onChange={e => setLength(e.target.value)}
                    />
                  </Col>
                  <Col xs={4}>
                    <Form.Label className="fw-semibold fs-9">Drum</Form.Label>
                    <Form.Select value={drumMaterial} onChange={e => setDrumMaterial(e.target.value)}>
                      {DRUM_MATERIALS.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={4}>
                    <Form.Label className="fw-semibold fs-9">Ø (mm)</Form.Label>
                    <Form.Select value={drumDiameter} onChange={e => setDrumDiameter(e.target.value)}>
                      {DRUM_DIAMETERS.map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
              ) : (
                <Row className="g-2 mb-3">
                  <Col xs={6}>
                    <Form.Label className="fw-semibold fs-9">Coil length (m)</Form.Label>
                    <Form.Control
                      type="number"
                      value={coilLength}
                      min={1}
                      onChange={e => setCoilLength(e.target.value)}
                    />
                  </Col>
                  <Col xs={6}>
                    <Form.Label className="fw-semibold fs-9">Coils / pallet</Form.Label>
                    <Form.Control
                      type="number"
                      value={coilCount}
                      min={1}
                      onChange={e => setCoilCount(e.target.value)}
                    />
                  </Col>
                </Row>
              )}

              {/* owner */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold fs-9 d-block">Destination</Form.Label>
                <ButtonGroup className="mb-2">
                  <Button
                    variant={owner === 'stock' ? 'success' : 'outline-success'}
                    size="sm"
                    onClick={() => setOwner('stock')}
                  >
                    STOCK
                  </Button>
                  <Button
                    variant={owner === 'client' ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => setOwner('client')}
                  >
                    Client
                  </Button>
                </ButtonGroup>
                {owner === 'client' && (
                  <Form.Control
                    placeholder="Client name"
                    value={customerName}
                    onChange={e => setCustomerName(e.target.value)}
                  />
                )}
              </Form.Group>

              <Row className="g-2 align-items-end">
                <Col xs={4}>
                  <Form.Label className="fw-semibold fs-9">Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    value={quantity}
                    min={1}
                    max={200}
                    onChange={e => setQuantity(e.target.value)}
                  />
                </Col>
                <Col xs={8}>
                  <Button
                    className="w-100"
                    variant="primary"
                    onClick={submit}
                    disabled={busy || !variant}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    {busy ? 'Adding…' : `Add ${quantity} to warehouse`}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* ── RESULT ───────────────────────────────────────── */}
        <Col lg={7}>
          <Card className="shadow-sm">
            <Card.Header className="bg-body-emphasis fw-semibold d-flex justify-content-between align-items-center">
              <span>Added this session</span>
              {created.length > 0 && (
                <Badge bg="primary">{created.length}</Badge>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {created.length === 0 ? (
                <div className="text-body-tertiary text-center py-5">
                  Nothing added yet — build stock on the left.
                </div>
              ) : (
                <Table hover responsive className="mb-0 fs-9 align-middle">
                  <thead>
                    <tr className="text-body-tertiary">
                      <th className="ps-3">Serial</th>
                      <th>Type</th>
                      <th>Cable</th>
                      <th className="text-end">Length</th>
                      <th className="text-end">Weight</th>
                      <th>Owner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {created.map(it => (
                      <tr key={it.serial}>
                        <td className="ps-3 font-monospace">{it.serial}</td>
                        <td>
                          <Badge bg={it.type === 'europallet' ? 'info' : 'warning'} className="text-uppercase">
                            {it.type === 'europallet' ? 'pallet' : 'drum'}
                          </Badge>
                        </td>
                        <td>{it.cableName}</td>
                        <td className="text-end">{fmt(it.lengthMeters)} m</td>
                        <td className="text-end">{fmt(Math.round(it.weightKg))} kg</td>
                        <td>
                          <Badge bg={it.owner === 'client' ? 'primary' : 'success'}>
                            {it.owner === 'client' ? 'CLIENT' : 'STOCK'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
